import {useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {omneticApi} from '@dms/api/base';
import {notificationTypes} from '@dms/shared';

import {useDebouncedCallback} from 'shared/web';

const REFRESH_DEBOUNCE_MS = 500;

// Every type below can move a task between tabs or change the badge count; commenting cannot.
const TASK_CENTER_NOTIFICATION_TYPES: string[] = [
  notificationTypes.TASK_CREATED,
  notificationTypes.TASK_UPDATED,
  notificationTypes.TASK_REPORTER_CHANGED,
  notificationTypes.TASK_DUE_DATE_NOTICE,
];

/**
 * Keeps the task center in sync with tasks assigned or changed by other users.
 *
 * RTK Query only invalidates on the mutation the same browser tab issued, so a task another user
 * assigns never reaches this user's lists. The backend already notifies the assignee, so this treats
 * the notification as a doorbell and lets the endpoints read the state back: `TaskList` covers all
 * three tabs and the homepage card, `TaskCounter` the red badge.
 *
 * MUST be mounted where it outlives the popup — the popup body unmounts on close, and the badge is
 * mounted for the whole session. See `TaskCenter`, its only call site.
 */
export function useRefreshTaskCenter() {
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);

  useEffect(function markUnmountedOnCleanup() {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refreshTaskCenter = useDebouncedCallback(() => {
    // A notification arriving just before unmount would otherwise still fire the debounced timer.
    if (!isMountedRef.current) {
      return;
    }
    dispatch(omneticApi.util.invalidateTags(['TaskList', 'TaskCounter']));
  }, REFRESH_DEBOUNCE_MS);

  useSubscribeToNotificationsQuery((notification) => {
    if (!TASK_CENTER_NOTIFICATION_TYPES.includes(notification.type)) {
      return;
    }

    refreshTaskCenter();
  });
}
